import { useEffect, useRef, useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

export const OptionListEditor = ({ options, onChange, error }) => {
  const list = options.length ? options : [''];
  const [focusIndex, setFocusIndex] = useState(null);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (focusIndex !== null) {
      inputRefs.current[focusIndex]?.focus();
      setFocusIndex(null);
    }
  }, [focusIndex, list.length]);

  const update = (next) => onChange(next);

  const setAt = (i, value) => {
    const next = [...list];
    next[i] = value;
    update(next);
  };

  const insertAfter = (i) => {
    const next = [...list];
    next.splice(i + 1, 0, '');
    update(next);
    setFocusIndex(i + 1);
  };

  const removeAt = (i) => {
    const next = list.filter((_, idx) => idx !== i);
    update(next.length ? next : ['']);
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    update(next);
  };

  const handlePaste = (i, e) => {
    const text = e.clipboardData.getData('text');
    const lines = text.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    if (lines.length <= 1) return;
    e.preventDefault();
    const next = [...list];
    next.splice(i, 1, ...lines);
    update(next);
    setFocusIndex(i + lines.length - 1);
  };

  const filled = list.map(o => o.trim()).filter(Boolean);
  const lastDupIndex = list.reduce((acc, o, i) => {
    const t = o.trim();
    return t && list.findIndex(x => x.trim() === t) !== i ? i : acc;
  }, -1);

  return (
    <div data-testid="option-list-editor">
      <div className="flex items-center justify-between">
        <Label>Options</Label>
        <span className="text-xs text-slate-500" data-testid="option-count">
          {filled.length} {filled.length === 1 ? 'option' : 'options'}
        </span>
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-2">Each box is one option shown to the client.</p>
      <div className="space-y-2">
        {list.map((opt, i) => (
          <div key={i} className="flex items-center gap-1" data-testid={`option-row-${i}`}>
            <Input
              ref={el => (inputRefs.current[i] = el)}
              value={opt}
              onChange={(e) => setAt(i, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); insertAfter(i); }
              }}
              onPaste={(e) => handlePaste(i, e)}
              placeholder={`Option ${i + 1}`}
              className={i === lastDupIndex ? 'border-amber-400 bg-amber-50' : ''}
              data-testid={`option-input-${i}`}
            />
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`option-move-up-${i}`}>
              <ChevronUp className="w-4 h-4" />
            </Button>
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => move(i, 1)} disabled={i === list.length - 1} data-testid={`option-move-down-${i}`}>
              <ChevronDown className="w-4 h-4" />
            </Button>
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-red-500" onClick={() => removeAt(i)} data-testid={`option-remove-${i}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
      {lastDupIndex >= 0 && (
        <p className="text-xs text-amber-600 mt-1" data-testid="option-duplicate-warning">Duplicate option text</p>
      )}
      {error && <p className="text-xs text-red-600 mt-1" data-testid="option-error">{error}</p>}
      <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => insertAfter(list.length - 1)} data-testid="add-option-btn">
        <Plus className="w-3 h-3 mr-1" />
        Add another option
      </Button>
    </div>
  );
};
